import { useState, useContext } from 'react';
import {
    CloudDownload,
    PersonOutlineOutlined,
    Replay,
    ChevronRight,
    ExpandMore,
} from '@mui/icons-material';
import { Box, Link } from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AppContext, SttChipPForm } from '../../../sporttia/all';
import SttGroupMemberDialog from '../../../sporttia/dialogs/groupMemberDialog/SttGroupMemberDialog';
import PaymentModal from '../../../components/paymentModal/PaymentModal';
import SttMshipDialog from '../../../sporttia/dialogs/SttMshipDialog';
import { formatPriceByLocale, fullName } from '../../../lib/utils';
import { useInteractionsFiles } from '../../../lib/hooks';
import { SttTopFilteringControls } from '../../../sporttia/SttTopFilteringControls';
import useGroupsService from '../../../services/GroupsService';
import { GROUP_MEMBERS } from '../../../lib/queryKeys';
import SttCachedTable from '../../../sporttia/SttCachedTable';
import { Group } from '../../../types/api/models';
import { DetailsId, DownloadFileFormat } from '../../../types/components';
import { GetGroupMembers200 } from '../../../types/api/paths/Group';
import DiscountTooltip from '../../../components/discounts/DiscountTooltip';
import { DefaultListParams } from '../../../types/api/utils';
import type { Payment } from '../../../types/models/Payment';
import type { GroupMember } from '../../../types/models/GroupMember';
import type { Mship } from '../../../types/models/Mship';
import { filtersToApi } from './utils/utils';
import type { Filters } from './utils/utils';
import { PERMISSIONS } from '../../../lib/permissions';

type GroupMembersProps = {
    item: Group;
    haveFees: boolean;
    filters: Filters;
    onFilterChange: (filters: Filters) => void;
};

/**
 * GroupMembers : list of members for this Group
 */
export default function GroupMembers({
    item,
    haveFees,
    filters,
    onFilterChange,
}: GroupMembersProps) {
    const { t } = useTranslation();
    const cxt = useContext(AppContext)!;

    const { downloadFile } = useInteractionsFiles();

    const [openGroupMemberDialog, setOpenGroupMemberDialog] = useState(false);
    const [groupMemberDialogId, setGroupMemberDialogId] = useState<DetailsId>();
    const [groupMemberTrash, setGroupMemberTrash] = useState<boolean>();
    const [idPayment, setIdPayment] = useState<number>();
    const [openMshipDialog, setOpenMshipDialog] = useState(false);
    const [selectedMshipId, setSelectedMshipId] = useState<number>();

    const groupsService = useGroupsService();

    const relationLabels: Record<string, string> = {
        OFFSPRING: t('offspring'),
        PARENT: t('partner'),
        PARTNER: t('partner'),
        OTHER: t('groups.beneficiaries.other'),
    };

    const queryClient = useQueryClient();

    const queryParams = { ...filtersToApi(filters), id: item.id };
    const queryKey = [GROUP_MEMBERS, queryParams];

    // Enrollment is group member payment
    function updatePayment(payment: Payment) {
        queryClient.setQueriesData<GetGroupMembers200 | undefined>(
            { queryKey },
            (old: any) => {
                if (!old) {
                    return undefined;
                }

                return {
                    ...old,
                    rows: old.rows.map((row: any) =>
                        row.enrollment?.id === payment.id
                            ? {
                                  ...row,
                                  enrollment: {
                                      ...row.enrollment,
                                      ...payment,
                                  },
                              }
                            : row,
                    ),
                };
            },
        );
    }

    function updateMship(mship: Mship | any) {
        queryClient.setQueriesData<GetGroupMembers200 | undefined>(
            { queryKey },
            (old: any) => {
                if (!old) {
                    return undefined;
                }

                return {
                    ...old,
                    rows: old.rows.map((row: any) => {
                        if (row.user?.mship?.id === mship.id) {
                            return {
                                ...row,
                                user: {
                                    ...row.user,
                                    mship: {
                                        ...row.user.mship,
                                        ...mship,
                                    },
                                },
                            };
                        }

                        return row;
                    }),
                };
            },
        );
    }

    function download(format: DownloadFileFormat) {
        if (format === 'pdf') {
            downloadFile(
                `/groups/${item.id}/members.pdf`,
                'application/pdf',
                filtersToApi(filters),
                `${item.name}.${format}`,
            );
        }

        if (format === 'csv') {
            downloadFile(
                `/groups/${item.id}/members.csv`,
                'text/csv',
                filtersToApi(filters),
                `${item.name}.${format}`,
            );
        }
    }

    return (
        <>
            <SttTopFilteringControls
                mb={2}
                fields={[
                    {
                        type: 'text',
                        caption: t('User'),
                        name: 'name',
                        xs: 12,
                        sm: 6,
                        md: 4,
                        lg: 3,
                    },
                ]}
                extraFields={[
                    {
                        name: 'isActive',
                        type: 'select',
                        caption: t('Active'),
                        value: filters.isActive,
                        sm: 5,
                        options: [
                            {
                                caption: t('All'),
                                value: 0,
                            },
                            {
                                caption: t('OnlyActiveOnes'),
                                value: 1,
                            },
                        ],
                    },
                    {
                        name: 'upToDateWithPayments',
                        type: 'select',
                        caption: t('Payments'),
                        value: filters.upToDateWithPayments,
                        sm: 5,
                        options: [
                            {
                                caption: t(
                                    'page.sc.groups.upToDateWithPayments',
                                ),
                                value: 1,
                            },
                            {
                                caption: t('All'),
                                value: 0,
                            },
                        ],
                    },
                    {
                        type: 'selectPeriod',
                        sm: 5,
                        selectCaption: t('groupMemberSubscriptionFilter'),
                        selectName: 'subscriptionType',
                        selectValue: filters.subscriptionType,
                        selectOptions: [
                            {
                                caption: t('groupMemberSubscription'),
                                value: 'inscription',
                            },
                            {
                                caption: t('groupMemberCancelation'),
                                value: 'unsubscription',
                            },
                            {
                                caption: t('All'),
                                value: 'all',
                            },
                        ],
                        nameIni: 'subscriptionIni',
                        valIni: filters.subscriptionIni,
                        nameEnd: 'subscriptionEnd',
                        valEnd: filters.subscriptionEnd,
                        disableOnValue: 'all',
                    },
                ]}
                mainAction={
                    item.type !== 'SPORT'
                        ? {
                              type: 'create',
                              permission: PERMISSIONS.GROUP.MODIFY,
                              onClick: () => {
                                  setOpenGroupMemberDialog(true);
                                  setGroupMemberDialogId('create');
                                  setGroupMemberTrash(false);
                              },
                          }
                        : null
                }
                menu={[
                    {
                        caption: `${t('Download')} PDF`,
                        onClick: () => download('pdf'),
                    },
                    {
                        caption: `${t('Download')} CSV`,
                        onClick: () => download('csv'),
                    },
                ]}
                menuIcon={<CloudDownload />}
                onFilter={onFilterChange}
                trashAction
            />
            <SttCachedTable
                queryKey={GROUP_MEMBERS}
                queryFn={(value: typeof queryParams & DefaultListParams) => {
                    const { id, ...params } = value;
                    return groupsService.getGroupMembers(id, params);
                }}
                queryParams={queryParams}
                prefetching
                columns={[
                    {
                        title: t('Name'),
                        type: 'link',
                        field: 'name',
                        align: 'left',
                        value: (row: GroupMember) => (
                            <>
                                <Link
                                    onClick={() => {
                                        setOpenGroupMemberDialog(true);
                                        setGroupMemberDialogId(row.id);
                                        setGroupMemberTrash(row.trash);
                                    }}
                                    variant="body2"
                                    sx={{
                                        color: 'var(--brand-text)',
                                        textDecoration: 'none',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            textDecoration: 'underline',
                                        },
                                    }}
                                >
                                    {fullName(row.user)}
                                </Link>
                                {row.notes ? (
                                    <Box
                                        mt={1}
                                        style={{ color: 'var(--destructive)' }}
                                    >
                                        {row.notes}
                                    </Box>
                                ) : null}
                                {row.beneficiaries?.count ? (
                                    <Box
                                        mt={1}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                        }}
                                    >
                                        <SimpleTreeView
                                            slots={{
                                                collapseIcon: ExpandMore,
                                                expandIcon: ChevronRight,
                                            }}
                                        >
                                            <TreeItem
                                                itemId="0"
                                                label={`${t(
                                                    'Beneficiaries',
                                                )} - ${
                                                    row.beneficiaries.count
                                                }`}
                                            >
                                                {row.beneficiaries.rows?.map(
                                                    (beneficiary) => (
                                                        <TreeItem
                                                            key={beneficiary.id}
                                                            itemId={beneficiary.id.toString()}
                                                            label={`${
                                                                beneficiary.name
                                                            } - ${
                                                                relationLabels[
                                                                    beneficiary.holderRelation?.toUpperCase() ??
                                                                        ''
                                                                ] ??
                                                                beneficiary.holderRelation ??
                                                                ''
                                                            }`}
                                                        />
                                                    ),
                                                )}
                                            </TreeItem>
                                        </SimpleTreeView>
                                    </Box>
                                ) : null}

                                <br />
                            </>
                        ),
                    },
                    {
                        title: t('Inscription'),
                        value: (row: GroupMember) =>
                            row.enrollment &&
                            (row.enrollment.refund ? (
                                <Box
                                    px={1}
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                >
                                    <Link
                                        onClick={() => {
                                            setIdPayment(row.enrollment?.id);
                                        }}
                                        sx={{
                                            color: 'var(--brand-text)',
                                            textDecoration: 'none',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                textDecoration: 'underline',
                                            },
                                        }}
                                    >
                                        {formatPriceByLocale(
                                            row.enrollment.price,
                                            cxt.sc?.city?.country?.currency,
                                        )}
                                    </Link>

                                    <Box mx={1}>
                                        <SttChipPForm
                                            pf={row.enrollment.paymentForm}
                                        />
                                    </Box>

                                    <Replay fontSize="small" />
                                </Box>
                            ) : (
                                <Box
                                    px={1}
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                >
                                    <Link
                                        onClick={() => {
                                            setIdPayment(row.enrollment?.id);
                                        }}
                                        sx={{
                                            color: 'var(--brand-text)',
                                            textDecoration: 'none',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                textDecoration: 'underline',
                                            },
                                        }}
                                    >
                                        {formatPriceByLocale(
                                            row.enrollment.price,
                                            cxt.sc?.city?.country?.currency,
                                        )}
                                    </Link>

                                    <Box mx={1}>
                                        <SttChipPForm
                                            pf={row.enrollment.paymentForm}
                                        />
                                    </Box>

                                    <Box width={20} height={20} />
                                </Box>
                            )),
                    },
                    {
                        title: t('Period'),
                        type: 'period',
                        value: (row: GroupMember) => ({
                            ini: row.ini,
                            end: row.end,
                        }),
                    },
                    {
                        title: `${t('PaymentForm')} (${t('Fee')})`,
                        value: (row: GroupMember) =>
                            haveFees &&
                            ['ABONADOS', 'NORMAL', 'FAMILY'].includes(
                                row.group?.type ?? '',
                            ) ? (
                                <SttChipPForm pf={row.paymentFormFee} />
                            ) : (
                                ''
                            ),
                    },
                    {
                        title: '',
                        value: (row: GroupMember) =>
                            row.discounts && (
                                <DiscountTooltip discounts={row.discounts} />
                            ),
                    },
                    {
                        title: '',
                        value: (row: GroupMember) => (
                            <PersonOutlineOutlined
                                fontSize="small"
                                sx={{ cursor: 'pointer' }}
                                onClick={() => {
                                    setOpenMshipDialog(true);
                                    setSelectedMshipId(row.user?.mship?.id);
                                }}
                            />
                        ),
                    },
                ]}
            />

            {openGroupMemberDialog && (
                <SttGroupMemberDialog
                    idGroupMember={groupMemberDialogId}
                    trash={groupMemberTrash}
                    group={item}
                    onClose={() => {
                        setOpenGroupMemberDialog(false);
                        setGroupMemberDialogId(null);
                    }}
                    onUpdateMember={() => {
                        // Invalidate GroupMembers table query (current Miembros tab)
                        queryClient.invalidateQueries({ queryKey });

                        // Invalidate GroupFees queries (Cuotas tab)
                        // This invalidates all queries starting with the group members endpoint
                        queryClient.invalidateQueries({
                            queryKey: [`/groups/${item.id}/members`],
                        });

                        setOpenGroupMemberDialog(false);
                        setGroupMemberDialogId(null);
                    }}
                />
            )}

            {idPayment && (
                <PaymentModal
                    idPayment={idPayment}
                    onClose={() => setIdPayment(undefined)}
                    onPay={(payment: any) => {
                        updatePayment(payment);
                    }}
                />
            )}

            <SttMshipDialog
                open={openMshipDialog}
                mshipId={selectedMshipId}
                onClose={() => {
                    setOpenMshipDialog(false);
                }}
                onSave={updateMship}
            />
        </>
    );
}
